import { useCallback, useMemo } from 'react';

import { useCurrentUser } from '@app-velocity/libs/auth';
import { MessageBus } from '@app-velocity/message-bus';

import { useGetReportPermissionsQuery } from '../../query/useGetReportPermissionsQuery';

const messageBus = new MessageBus('users');

export const usePrototypePermissions = (shareToken: string) => {
  const currentUser = useCurrentUser();

  const { data, error } = useGetReportPermissionsQuery(shareToken || '');

  const hasAccessToSims = useMemo(() => {
    if (!currentUser) return false;
    if (error) return false;
    if (data?.userRole) return true;

    return false;
  }, [data, error, currentUser]);

  const canManageAccess = useMemo(() => {
    if (!currentUser || !data) return false;
    const { userRole, subscriptionTier, isOwner } = data;
    if (!subscriptionTier || subscriptionTier === 'FREE' || !userRole)
      return false;
    if (['ADMIN', 'OWNER'].includes(userRole)) return true;
    if (isOwner) return true;

    return false;
  }, [currentUser, data]);

  const canRunSimulations = useCallback(
    (amount: number) => {
      if (error || !data || !currentUser) return false;

      const { simsLeft, userRole, teamId, subscriptionTier: tier } = data;

      if (!userRole || !tier) return false;

      const simulationsLeft = simsLeft || 0;

      switch (tier) {
        case 'FREE': {
          if (amount > 1) {
            messageBus.publish('modal:higher-plan-feature-only:open');

            return false;
          }

          if (simulationsLeft >= 1) return true;

          messageBus.publish('modal:no-more-credits:open', {
            role: userRole,
            tier,
            teamId,
          });

          return false;
        }

        default: {
          if (simulationsLeft >= amount) return true;
          messageBus.publish('modal:no-more-credits:open', {
            role: userRole,
            tier,
            teamId,
          });

          return false;
        }
      }
    },
    [data, error, currentUser]
  );

  const canManageReportConfig = useMemo(() => {
    if (!currentUser || !data) return false;
    const { isOwner, userRole } = data;

    if (!userRole) return false;

    if (isOwner) return true;
    if (['ADMIN', 'OWNER'].includes(userRole)) return true;

    return false;
  }, [currentUser, data]);

  return {
    hasAccessToSims,
    canRunSimulations,
    canManageAccess,
    canManageReportConfig,
    teamId: data?.teamId,
  };
};
